import { doc, getDoc, setDoc, updateDoc, deleteField } from 'firebase/firestore';
import { db } from '../services/firebase';
import type {
    User,
    UserMealPreferences,
    AiMealPreferencesInput,
    Chef,
    Recipe,
} from '../types';
import { EMPTY_DIET_TYPES, normalizeDietPreference, normalizeDietTypes } from './dietTypes';

export function getDefaultUserMealPreferences(userId: string, user?: Partial<User>): UserMealPreferences {
    const now = new Date().toISOString();
    return {
        userId,
        favorites: { recipes: {}, chefs: {} },
        preferredDiet: normalizeDietPreference(user?.dietPreference),
        preferredDietTypes: normalizeDietTypes(user?.userDietTypes),
        inferred: {},
        createdAt: now,
        updatedAt: now,
    };
}

export async function ensureUserMealPreferences(userId: string, user?: Partial<User>): Promise<UserMealPreferences> {
    const ref = doc(db, 'userMealPreferences', userId);
    const snap = await getDoc(ref);
    if (snap.exists()) {
        return snap.data() as UserMealPreferences;
    }
    const defaults = getDefaultUserMealPreferences(userId, user);
    await setDoc(ref, defaults);
    return defaults;
}

export function isFavoriteRecipe(prefs: UserMealPreferences | null | undefined, recipeId: string): boolean {
    return !!prefs?.favorites?.recipes?.[recipeId];
}

export function isFavoriteChef(prefs: UserMealPreferences | null | undefined, chefId: string): boolean {
    return !!prefs?.favorites?.chefs?.[chefId];
}

export async function toggleFavoriteRecipe(userId: string, recipeId: string, user?: Partial<User>): Promise<void> {
    await ensureUserMealPreferences(userId, user);
    const ref = doc(db, 'userMealPreferences', userId);
    const snap = await getDoc(ref);
    const prefs = snap.data() as UserMealPreferences;
    const isFav = !!prefs.favorites?.recipes?.[recipeId];
    const now = new Date().toISOString();

    if (isFav) {
        await updateDoc(ref, {
            [`favorites.recipes.${recipeId}`]: deleteField(),
            updatedAt: now,
        });
    } else {
        await updateDoc(ref, {
            [`favorites.recipes.${recipeId}`]: { addedAt: now, source: 'manual' as const },
            updatedAt: now,
        });
    }
}

export async function toggleFavoriteChef(userId: string, chefId: string, user?: Partial<User>): Promise<void> {
    await ensureUserMealPreferences(userId, user);
    const ref = doc(db, 'userMealPreferences', userId);
    const snap = await getDoc(ref);
    const prefs = snap.data() as UserMealPreferences;
    const isFav = !!prefs.favorites?.chefs?.[chefId];
    const now = new Date().toISOString();

    if (isFav) {
        await updateDoc(ref, {
            [`favorites.chefs.${chefId}`]: deleteField(),
            updatedAt: now,
        });
    } else {
        await updateDoc(ref, {
            [`favorites.chefs.${chefId}`]: { addedAt: now, source: 'manual' as const },
            updatedAt: now,
        });
    }
}

export async function syncPreferredDietFromUser(userId: string, user: User): Promise<void> {
    await ensureUserMealPreferences(userId, user);
    await updateDoc(doc(db, 'userMealPreferences', userId), {
        preferredDiet: normalizeDietPreference(user.dietPreference),
        preferredDietTypes: normalizeDietTypes(user.userDietTypes),
        updatedAt: new Date().toISOString(),
    });
}

export function buildAiMealPreferencesPayload(
    prefs: UserMealPreferences,
    user: User,
    _recipes?: Recipe[],
    _chefs?: Chef[],
): AiMealPreferencesInput {
    return {
        favoriteRecipeIds: Object.keys(prefs.favorites?.recipes ?? {}),
        favoriteChefIds: Object.keys(prefs.favorites?.chefs ?? {}),
        preferredDiet: prefs.preferredDiet ?? normalizeDietPreference(user.dietPreference),
        preferredDietTypes: prefs.preferredDietTypes ?? normalizeDietTypes(user.userDietTypes) ?? { ...EMPTY_DIET_TYPES },
    };
}
